import React, { useState, useEffect } from "react";
import { Plus, Lock } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../utils/api";
import DataTable from "../components/DataTable";
import Modal from "../components/Modal";
import { Field, Input, Textarea, Select, FileInput, FormActions } from "../components/FormField";
import { useAuth } from "../../context/AuthContext";
import ImagePositionPicker from "../components/ImagePositionPicker";

const PAGES = ["home", "about", "solutions", "contact"];
const SECTIONS = {
  home:      ["hero", "stats", "categories", "cta-banner", "solutions-preview"],
  about:     ["hero", "story", "values", "timeline", "stats"],
  solutions: ["hero", "list"],
  contact:   ["info"],
};
const HINTS = {
  hero:                "Main banner at the top of the page",
  story:               "About Us — Our Story section with image",
  values:              "Our Foundation — Vision, Mission, Values cards",
  timeline:            "Milestones / Journey timeline",
  stats:               "Statistics row (Years, Products, Clients...)",
  categories:          "Product category cards grid",
  "cta-banner":        "Call-to-action banner with background image",
  "solutions-preview": "Solutions teaser at the bottom of home page",
  list:                "Solutions list items",
  info:                "Contact address, phone, email, hours",
};
const BASE = process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:5000";
const empty = { page: "home", section: "", title: "", subtitle: "", body: "", order: "" };

export default function AdminContent() {
  const { can } = useAuth();
  const canCreate = can('content', 'create');
  const canEdit   = can('content', 'edit');
  const canDelete = can('content', 'delete');
  const [content, setContent]   = useState([]);
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(empty);
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [imgPosition, setImgPosition] = useState('50% 50%');
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [pageFilter, setPageFilter] = useState("");

  const load = () => {
    api.get(`/content${pageFilter ? `?page=${pageFilter}` : ""}`)
      .then(r => setContent(r.data.content || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, [pageFilter]);

  const openCreate = () => { setEditing(null); setForm(empty); setFile(null); setPreview(null); setImgPosition('50% 50%'); setErrors({}); setModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ page: c.page, section: c.section, title: c.title || "", subtitle: c.subtitle || "", body: c.body || "", order: c.order ?? "" });
    setFile(null);
    setPreview(c.image ? (c.image.startsWith("http") ? c.image : `${BASE}${c.image}`) : null);
    setImgPosition(c.extraData?.objectPosition || '50% 50%');
    setErrors({});
    setModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.section.trim()) e.section = "Section is required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("page", form.page);
      fd.append("section", form.section.trim());
      fd.append("title", form.title);
      fd.append("subtitle", form.subtitle);
      fd.append("body", form.body);
      fd.append("order", form.order || 0);
      if (file) fd.append("images", file);
      // Save objectPosition into extraData
      const existingExtraData = editing?.extraData || {};
      fd.append("extraData", JSON.stringify({ ...existingExtraData, objectPosition: imgPosition }));
      await api.post("/content", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Content saved");
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving content");
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this content block?")) return;
    try { await api.delete(`/content/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Error deleting"); }
  };

  const columns = [
    { key: "page", label: "Page", isTitle: true,
      render: v => <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 capitalize">{v}</span>
    },
    { key: "section", label: "Section",
      render: v => <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{v}</span>
    },
    { key: "title", label: "Title",
      render: v => v || <span className="text-gray-300 italic text-xs">—</span>
    },
    { key: "image", label: "Image", sortable: false,
      render: v => v
        ? <img src={v.startsWith("http") ? v : `${BASE}${v}`} alt="" className="w-12 h-8 object-cover rounded-lg border border-gray-100" />
        : <span className="text-gray-300 text-xs">—</span>
    },
    { key: "images", label: "Gallery", sortable: false,
      render: v => v?.length > 0
        ? <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{v.length} image{v.length > 1 ? "s" : ""}</span>
        : <span className="text-gray-300 text-xs">—</span>
    },
  ];

  const summaryFields = [
    { key: 'total', label: 'Total Blocks', color: '#4F8EF7', render: d => d.length },
    { key: 'home', label: 'Home', color: '#10B981', render: d => d.filter(c => c.page === 'home').length },
    { key: 'solutions', label: 'Solutions', color: '#F59E0B', render: d => d.filter(c => c.page === 'solutions').length },
    { key: 'contact', label: 'Contact', color: '#8B5CF6', render: d => d.filter(c => c.page === 'contact').length },
  ];

  const sectionOptions = SECTIONS[form.page] || [];

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Content Manager</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage text and images for each page section.</p>
        </div>
        <button onClick={openCreate}
          disabled={!canCreate}
          title={!canCreate ? 'No permission to create content' : ''}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: canCreate ? 'linear-gradient(135deg, #B8A830, #d4af37)' : '#9CA3AF' }}>
          {canCreate ? <Plus size={14} /> : <Lock size={14} />} Add Content
        </button>
      </div>

      {/* page filter pills */}
      <div className="flex gap-2 flex-wrap">
        {["", ...PAGES].map(p => (
          <button key={p} onClick={() => setPageFilter(p)}
            className={`px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors capitalize
              ${pageFilter === p ? "bg-yellow-600 text-white border-yellow-600" : "border-gray-200 text-gray-500 hover:border-yellow-400 hover:text-yellow-600"}`}>
            {p || "All Pages"}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={content}
        onEdit={canEdit ? openEdit : null}
        onDelete={canDelete ? handleDelete : null}
        searchKeys={["page", "section", "title"]}
        summaryFields={summaryFields}
        defaultView="table"
      />

      {modal && (
        <Modal title={editing ? "Edit Content Block" : "Add Content Block"} onClose={() => setModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700">
              The <strong>page + section</strong> combination uniquely identifies each content block.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Page" required>
                <Select value={form.page} onChange={e => setForm({ ...form, page: e.target.value, section: "" })}>
                  {PAGES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </Select>
              </Field>
              <Field label="Section" required hint={HINTS[form.section] || "Select a section"} error={errors.section}>
                <Select value={form.section} onChange={e => setForm({ ...form, section: e.target.value })} error={errors.section}>
                  <option value="">— Select section —</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Title">
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Enter section title…" />
            </Field>
            <Field label="Subtitle">
              <Input value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} placeholder="Enter subtitle…" />
            </Field>
            <Field label="Body Text">
              <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="Enter body text…" rows={4} />
            </Field>
            <Field label="Display Order" hint="Lower numbers appear first">
              <Input type="number" min="0" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} placeholder="0" />
            </Field>
            <FileInput label="Section Image"
              onChange={e => { const f = e.target.files[0]; setFile(f); setPreview(f ? URL.createObjectURL(f) : null); if (f) setImgPosition('50% 50%'); }}
              preview={preview} />
            {preview && (
              <ImagePositionPicker
                src={preview}
                position={imgPosition}
                onChange={setImgPosition}
              />
            )}
            <FormActions onCancel={() => setModal(false)} loading={loading} submitLabel={editing ? "Update Content" : "Save Content"} />
          </form>
        </Modal>
      )}
    </div>
  );
}
